import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { inventoryAPI, locationsAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Trash2, Upload, FileSpreadsheet, AlertCircle, CheckCircle2, FileUp, X } from 'lucide-react';
import * as XLSX from 'xlsx';

const COLUMNS = [
  { key: 'item_name', label: 'Item Name / SKU *', width: '250px', type: 'text', placeholder: 'Search item name or SKU...' },
  { key: 'quantity', label: 'Quantity *', width: '100px', type: 'number', placeholder: '0' },
  { key: 'purchase_cost', label: 'Cost/Unit ($) *', width: '120px', type: 'number', placeholder: '0.00' },
  { key: 'purchase_date', label: 'Purchase Date', width: '150px', type: 'date', placeholder: '' },
  { key: 'lot_number', label: 'Lot Number', width: '150px', type: 'text', placeholder: 'LOT-...' },
  { key: 'ndc_number', label: 'NDC Number', width: '150px', type: 'text', placeholder: '12345-6789-01' },
  { key: 'expiration_date', label: 'Expiration Date', width: '150px', type: 'date', placeholder: '' },
  { key: 'location_name', label: 'Location', width: '160px', type: 'text', placeholder: 'Select location...' },
];

const MAPPABLE_FIELDS = [
  { key: '', label: '-- Skip --' },
  { key: 'item_name', label: 'Item Name / SKU' },
  { key: 'quantity', label: 'Quantity' },
  { key: 'purchase_cost', label: 'Cost/Unit ($)' },
  { key: 'purchase_date', label: 'Purchase Date' },
  { key: 'lot_number', label: 'Lot Number' },
  { key: 'ndc_number', label: 'NDC Number' },
  { key: 'expiration_date', label: 'Expiration Date' },
  { key: 'location_name', label: 'Location' },
];

const emptyRow = () => ({
  id: crypto.randomUUID(),
  item_name: '',
  quantity: '',
  purchase_cost: '',
  purchase_date: new Date().toISOString().split('T')[0],
  lot_number: '',
  ndc_number: '',
  expiration_date: '',
  location_name: '',
  _matched_item: null,
  _matched_location: null,
  _status: 'editing',
  _error: '',
  _error_field: '',
});

// Try to auto-map a CSV header to a batch field
const autoMapHeader = (header) => {
  const h = header.toLowerCase().trim();
  if (h.includes('item') || h.includes('product') || h.includes('name') || h.includes('sku') || h.includes('description') || h.includes('medication') || h.includes('drug')) return 'item_name';
  if (h.includes('qty') || h.includes('quantity') || h.includes('amount') || h.includes('units') || h.includes('count')) return 'quantity';
  if (h.includes('cost') || h.includes('price') || h.includes('unit price') || h.includes('unit_price')) return 'purchase_cost';
  if (h.includes('purchase') && h.includes('date')) return 'purchase_date';
  if (h === 'date' || h === 'fecha') return 'purchase_date';
  if (h.includes('lot') || h.includes('batch')) return 'lot_number';
  if (h.includes('ndc')) return 'ndc_number';
  if (h.includes('expir') || h.includes('exp date') || h.includes('exp_date')) return 'expiration_date';
  if (h.includes('location') || h.includes('site') || h.includes('branch') || h.includes('store') || h.includes('ubicacion')) return 'location_name';
  return '';
};

// Parse date from various formats
const parseDate = (val) => {
  if (!val) return '';
  if (typeof val === 'number') {
    // Excel serial date
    const d = XLSX.SSF.parse_date_code(val);
    if (d) return `${d.y}-${String(d.m).padStart(2, '0')}-${String(d.d).padStart(2, '0')}`;
  }
  const s = String(val).trim();
  // Try ISO format YYYY-MM-DD
  const isoMatch = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (isoMatch) return `${isoMatch[1]}-${isoMatch[2].padStart(2, '0')}-${isoMatch[3].padStart(2, '0')}`;
  // Try MM/DD/YYYY (4-digit year)
  const usMatch = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (usMatch) return `${usMatch[3]}-${usMatch[1].padStart(2, '0')}-${usMatch[2].padStart(2, '0')}`;
  // Try MM/DD/YY (2-digit year)
  const usShort = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2})$/);
  if (usShort) {
    const yr = parseInt(usShort[3]);
    const fullYear = yr >= 50 ? 1900 + yr : 2000 + yr;
    return `${fullYear}-${usShort[1].padStart(2, '0')}-${usShort[2].padStart(2, '0')}`;
  }
  // Try DD-MM-YYYY or DD.MM.YYYY
  const euMatch = s.match(/^(\d{1,2})[-.](\d{1,2})[-.](\d{4})$/);
  if (euMatch) return `${euMatch[3]}-${euMatch[2].padStart(2, '0')}-${euMatch[1].padStart(2, '0')}`;
  return '';
};

const BatchImport = () => {
  const navigate = useNavigate();
  const { user, workingLocation } = useAuth();
  const [rows, setRows] = useState(() => Array.from({ length: 10 }, emptyRow));

  // Pre-fill location when workingLocation becomes available
  useEffect(() => {
    if (workingLocation && rows.every(r => !r.location_name)) {
      setRows(prev => prev.map(r => ({
        ...r,
        location_name: workingLocation.name || '',
        _matched_location: workingLocation,
      })));
    }
  }, [workingLocation]); // eslint-disable-line react-hooks/exhaustive-deps
  const [inventoryItems, setInventoryItems] = useState([]);
  const [locations, setLocations] = useState([]);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const tableRef = useRef(null);
  const [activeCell, setActiveCell] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [suggestionsRowId, setSuggestionsRowId] = useState(null);
  const suggestionsRef = useRef(null);
  const fileInputRef = useRef(null);

  // Column resize state
  const [colWidths, setColWidths] = useState(() => {
    const widths = {};
    COLUMNS.forEach(col => { widths[col.key] = parseInt(col.width); });
    return widths;
  });
  const resizing = useRef(null);

  // CSV/Excel mapping state
  const [mappingOpen, setMappingOpen] = useState(false);
  const [csvHeaders, setCsvHeaders] = useState([]);
  const [csvPreviewRows, setCsvPreviewRows] = useState([]);
  const [csvAllRows, setCsvAllRows] = useState([]);
  const [fieldMapping, setFieldMapping] = useState({});

  // Load all inventory items and locations
  useEffect(() => {
    const loadItems = async () => {
      try {
        const res = await inventoryAPI.getAll({ page: 1, page_size: 500, sort_by: 'name', sort_order: 'asc' });
        let items = res.data?.items || [];
        const totalPages = res.data?.total_pages || 1;
        for (let p = 2; p <= totalPages; p++) {
          const r2 = await inventoryAPI.getAll({ page: p, page_size: 500, sort_by: 'name', sort_order: 'asc' });
          items = items.concat(r2.data?.items || []);
        }
        setInventoryItems(items);
      } catch {
        toast.error('Failed to load inventory items');
      }
    };
    const loadLocations = async () => {
      try {
        const res = await locationsAPI.getAll();
        setLocations(res.data?.items || res.data || []);
      } catch {
        toast.error('Failed to load locations');
      }
    };
    loadItems();
    loadLocations();
  }, []);

  // Close suggestions on click outside
  useEffect(() => {
    const handler = (e) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(e.target)) {
        setSuggestions([]);
        setSuggestionsRowId(null);
      }
      if (locationSuggestionsRef.current && !locationSuggestionsRef.current.contains(e.target)) {
        setLocationSuggestions([]);
        setLocationSuggestionsRowId(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Column resize drag handlers
  useEffect(() => {
    const onMouseMove = (e) => {
      if (!resizing.current) return;
      const { key, startX, startWidth } = resizing.current;
      const delta = e.clientX - startX;
      const newWidth = Math.max(80, startWidth + delta);
      setColWidths(prev => ({ ...prev, [key]: newWidth }));
    };
    const onMouseUp = () => {
      if (resizing.current) {
        resizing.current = null;
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      }
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, []);

  const startResize = (e, colKey) => {
    e.preventDefault();
    e.stopPropagation();
    resizing.current = { key: colKey, startX: e.clientX, startWidth: colWidths[colKey] };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  // Match item name to inventory (exact match, case-insensitive, trimmed)
  const matchItem = (name, locationId) => {
    if (!name) return null;
    const lower = name.toLowerCase().trim();
    // If locationId provided, match item at that location first
    if (locationId) {
      const locMatch = inventoryItems.find(i =>
        i.location_id === locationId && (i.name.toLowerCase().trim() === lower || (i.sku && i.sku.toLowerCase().trim() === lower))
      );
      if (locMatch) return locMatch;
    }
    return inventoryItems.find(i =>
      i.name.toLowerCase().trim() === lower || (i.sku && i.sku.toLowerCase().trim() === lower)
    ) || null;
  };

  // Match location name (exact, case-insensitive)
  const matchLocation = (name) => {
    if (!name) return null;
    const lower = name.toLowerCase().trim();
    return locations.find(l => l.name.toLowerCase().trim() === lower) || null;
  };

  const [locationSuggestions, setLocationSuggestions] = useState([]);
  const [locationSuggestionsRowId, setLocationSuggestionsRowId] = useState(null);
  const locationSuggestionsRef = useRef(null);

  // Get location-filtered product suggestions
  const getItemSuggestions = (searchValue, rowId) => {
    if (searchValue.length < 2) return [];
    const lower = searchValue.toLowerCase();
    // Find the row's location for filtering
    const currentRow = rows.find(r => r.id === rowId);
    const locationId = currentRow?._matched_location?.id || workingLocation?.id;

    // First: items matching at the row's location
    let matches = [];
    if (locationId) {
      matches = inventoryItems.filter(i =>
        i.location_id === locationId &&
        (i.name.toLowerCase().includes(lower) || (i.sku && i.sku.toLowerCase().includes(lower)))
      );
    }
    // Fallback: if no location matches, search all items
    if (matches.length === 0) {
      matches = inventoryItems.filter(i =>
        i.name.toLowerCase().includes(lower) || (i.sku && i.sku.toLowerCase().includes(lower))
      );
    }
    return matches.slice(0, 10);
  };

  const updateRow = (rowId, field, value) => {
    // Handle suggestion dropdowns outside the setRows updater (must be side-effect-free)
    if (field === 'item_name') {
      const matches = getItemSuggestions(value, rowId);
      if (matches.length > 0) {
        setSuggestions(matches);
        setSuggestionsRowId(rowId);
      } else {
        setSuggestions([]);
        setSuggestionsRowId(null);
      }
    }
    if (field === 'location_name') {
      if (value.length >= 1) {
        const lower = value.toLowerCase();
        const matches = locations.filter(l => l.name.toLowerCase().includes(lower));
        setLocationSuggestions(matches);
        setLocationSuggestionsRowId(rowId);
      } else {
        setLocationSuggestions([]);
        setLocationSuggestionsRowId(null);
      }
    }

    setRows(prev => prev.map(r => {
      if (r.id !== rowId) return r;
      const updated = { ...r, [field]: value, _status: 'editing', _error: '', _error_field: '' };
      if (field === 'item_name') {
        const locId = updated._matched_location?.id;
        const lower = value.toLowerCase().trim();
        let matched = null;
        if (locId) {
          matched = inventoryItems.find(i => i.location_id === locId && (i.name.toLowerCase().trim() === lower || (i.sku && i.sku.toLowerCase().trim() === lower)));
        }
        if (!matched) {
          matched = inventoryItems.find(i => i.name.toLowerCase().trim() === lower || (i.sku && i.sku.toLowerCase().trim() === lower));
        }
        updated._matched_item = matched || null;
      }
      if (field === 'location_name') {
        const lower = value.toLowerCase().trim();
        updated._matched_location = locations.find(l => l.name.toLowerCase().trim() === lower) || null;
      }
      return updated;
    }));
  };

  // When clicking/focusing on an item_name field, show suggestions for existing text
  const handleItemFocus = (rowId, value) => {
    if (value && value.length >= 2) {
      const matches = getItemSuggestions(value, rowId);
      if (matches.length > 0) {
        setSuggestions(matches);
        setSuggestionsRowId(rowId);
      }
    }
  };

  // When clicking/focusing on a location field, show suggestions for existing text
  const handleLocationFocus = (rowId, value) => {
    if (value && value.length >= 1) {
      const lower = value.toLowerCase();
      const matches = locations.filter(l => l.name.toLowerCase().includes(lower));
      if (matches.length > 0) {
        setLocationSuggestions(matches);
        setLocationSuggestionsRowId(rowId);
      }
    }
  };

  const selectSuggestion = (rowId, item) => {
    setRows(prev => prev.map(r => {
      if (r.id !== rowId) return r;
      return { ...r, item_name: item.name, _matched_item: item, _status: 'editing', _error: '', _error_field: '' };
    }));
    setSuggestions([]);
    setSuggestionsRowId(null);
  };

  const selectLocationSuggestion = (rowId, loc) => {
    setRows(prev => prev.map(r => {
      if (r.id !== rowId) return r;
      const updated = { ...r, location_name: loc.name, _matched_location: loc, _status: 'editing', _error: '' };
      // Re-match item at new location
      if (updated.item_name) {
        updated._matched_item = matchItem(updated.item_name, loc.id);
      }
      return updated;
    }));
    setLocationSuggestions([]);
    setLocationSuggestionsRowId(null);
  };

  const addRows = (count = 5) => {
    setRows(prev => [...prev, ...Array.from({ length: count }, () => {
      const r = emptyRow();
      if (workingLocation) { r.location_name = workingLocation.name || ''; r._matched_location = workingLocation; }
      return r;
    })]);
  };

  const removeRow = (rowId) => {
    setRows(prev => {
      const filtered = prev.filter(r => r.id !== rowId);
      if (filtered.length === 0) {
        const r = emptyRow();
        if (workingLocation) { r.location_name = workingLocation.name || ''; r._matched_location = workingLocation; }
        return [r];
      }
      return filtered;
    });
  };

  const clearAll = () => {
    setRows(Array.from({ length: 10 }, () => {
      const r = emptyRow();
      if (workingLocation) { r.location_name = workingLocation.name || ''; r._matched_location = workingLocation; }
      return r;
    }));
    setImportResult(null);
  };

  // ============ CSV/Excel File Handling ============
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const data = new Uint8Array(evt.target.result);
        const wb = XLSX.read(data, { type: 'array', cellDates: true });
        const wsName = wb.SheetNames[0];
        const ws = wb.Sheets[wsName];
        const jsonData = XLSX.utils.sheet_to_json(ws, { header: 1, raw: false, defval: '' });

        if (jsonData.length < 2) {
          toast.error('File must have at least a header row and one data row');
          return;
        }

        const headers = jsonData[0].map(h => String(h).trim());
        const dataRows = jsonData.slice(1).filter(row => row.some(cell => cell !== ''));

        // Auto-map headers
        const autoMapping = {};
        headers.forEach((h, idx) => {
          const mapped = autoMapHeader(h);
          if (mapped && !Object.values(autoMapping).includes(mapped)) {
            autoMapping[idx] = mapped;
          } else {
            autoMapping[idx] = 'skip';
          }
        });

        setCsvHeaders(headers);
        setCsvPreviewRows(dataRows.slice(0, 5));
        setCsvAllRows(dataRows);
        setFieldMapping(autoMapping);
        setMappingOpen(true);
      } catch (err) {
        toast.error('Failed to parse file: ' + err.message);
      }
    };
    reader.readAsArrayBuffer(file);
    // Reset input so same file can be re-selected
    e.target.value = '';
  };

  const applyMapping = () => {
    // Build rows from CSV data using field mapping
    const newRows = csvAllRows.map(csvRow => {
      const row = emptyRow();
      Object.entries(fieldMapping).forEach(([colIdx, fieldKey]) => {
        if (!fieldKey || fieldKey === 'skip') return;
        let val = csvRow[parseInt(colIdx)] || '';
        if (fieldKey === 'purchase_date' || fieldKey === 'expiration_date') {
          val = parseDate(val);
        } else if (fieldKey === 'quantity') {
          val = String(parseInt(val) || 0);
        } else if (fieldKey === 'purchase_cost') {
          val = String(parseFloat(String(val).replace(/[$,]/g, '')) || 0);
        }
        row[fieldKey] = val;
      });

      // Match location from CSV
      if (row.location_name) {
        row._matched_location = matchLocation(row.location_name);
      }

      // Flag error if location is missing or doesn't exist
      if (!row.location_name) {
        row._status = 'error';
        row._error = 'Location is required';
        row._error_field = 'location_name';
      } else if (!row._matched_location) {
        row._status = 'error';
        row._error = `Location "${row.location_name}" not found`;
        row._error_field = 'location_name';
      }

      // Match item at matched location
      const locId = row._matched_location?.id;
      const matched = matchItem(row.item_name, locId);
      row._matched_item = matched;
      if (row.item_name && !matched && row._status !== 'error') {
        row._status = 'unmatched';
        row._error = 'Product not found in inventory';
        row._error_field = 'item_name';
      }

      return row;
    });

    setRows(newRows);
    setMappingOpen(false);
    setImportResult(null);

    const unmatched = newRows.filter(r => r._status === 'unmatched').length;
    const matched = newRows.filter(r => r._matched_item).length;
    if (unmatched > 0) {
      toast.warning(`${unmatched} product${unmatched !== 1 ? 's' : ''} not found in inventory (highlighted in red). ${matched} matched.`);
    } else {
      toast.success(`${matched} row${matched !== 1 ? 's' : ''} loaded and matched successfully`);
    }
  };

  const validateRows = () => {
    let valid = true;
    const updated = rows.map(r => {
      if (!r.item_name && !r.quantity && !r.purchase_cost) {
        return { ...r, _status: 'editing', _error: '', _error_field: '' };
      }
      if (!r.item_name) {
        valid = false;
        return { ...r, _status: 'error', _error: 'Item name required', _error_field: 'item_name' };
      }
      // Validate location exists
      if (r.location_name && !r._matched_location) {
        const locMatch = matchLocation(r.location_name);
        if (locMatch) {
          r = { ...r, _matched_location: locMatch };
        } else {
          valid = false;
          return { ...r, _status: 'error', _error: `Location "${r.location_name}" not found`, _error_field: 'location_name' };
        }
      }
      if (!r._matched_location && !workingLocation) {
        valid = false;
        return { ...r, _status: 'error', _error: 'Location required', _error_field: 'location_name' };
      }
      if (!r._matched_item) {
        const locId = r._matched_location?.id || workingLocation?.id;
        const match = matchItem(r.item_name, locId);
        if (match) {
          r = { ...r, _matched_item: match };
        } else {
          valid = false;
          return { ...r, _status: 'error', _error: 'Product not found in inventory', _error_field: 'item_name' };
        }
      }
      // Verify product belongs to the selected location
      const rowLocId = r._matched_location?.id || workingLocation?.id;
      if (r._matched_item && rowLocId && r._matched_item.location_id && r._matched_item.location_id !== rowLocId) {
        valid = false;
        return { ...r, _status: 'error', _error: 'Product does not exist at this location', _error_field: 'item_name' };
      }
      if (!r.quantity || parseInt(r.quantity) <= 0) {
        valid = false;
        return { ...r, _status: 'error', _error: 'Quantity must be > 0', _error_field: 'quantity' };
      }
      if (!r.purchase_cost || parseFloat(r.purchase_cost) <= 0) {
        valid = false;
        return { ...r, _status: 'error', _error: 'Cost must be > 0', _error_field: 'purchase_cost' };
      }
      return { ...r, _status: 'valid', _error: '', _error_field: '' };
    });
    setRows(updated);
    return valid;
  };

  const handleImport = async () => {
    if (!validateRows()) {
      toast.error('Please fix the highlighted errors before importing');
      return;
    }

    const dataRows = rows.filter(r => r._matched_item && r.quantity && r.purchase_cost);
    if (dataRows.length === 0) {
      toast.error('No valid rows to import');
      return;
    }

    setImporting(true);
    try {
      const payload = dataRows.map(r => ({
        inventory_item_id: r._matched_item.id,
        item_name: r._matched_item.name,
        quantity: parseInt(r.quantity),
        purchase_cost: parseFloat(r.purchase_cost),
        purchase_date: r.purchase_date || null,
        lot_number: r.lot_number || null,
        ndc_number: r.ndc_number || null,
        expiration_date: r.expiration_date || null,
        location_id: r._matched_location?.id || r._matched_item.location_id || workingLocation?.id || null,
      }));

      const res = await inventoryAPI.bulkImportBatches({ batches: payload });
      const result = res.data;

      setRows(prev => prev.map(r => {
        if (!r._matched_item) return r;
        const imported = result.imported?.find(imp => imp.inventory_item_id === r._matched_item.id && imp.quantity === parseInt(r.quantity));
        if (imported) return { ...r, _status: 'imported' };
        const failed = result.failed?.find(f => f.inventory_item_id === r._matched_item.id);
        if (failed) return { ...r, _status: 'error', _error: failed.error };
        return { ...r, _status: 'imported' };
      }));

      setImportResult(result);
      toast.success(`Successfully imported ${result.imported_count} batch${result.imported_count !== 1 ? 'es' : ''}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import batches');
    } finally {
      setImporting(false);
    }
  };

  const handleKeyDown = (e, rowIndex, colIndex) => {
    if (e.key === 'Tab' || e.key === 'Enter') {
      e.preventDefault();
      const nextCol = e.shiftKey ? colIndex - 1 : colIndex + 1;
      if (nextCol >= COLUMNS.length) {
        if (rowIndex + 1 < rows.length) {
          setActiveCell({ row: rowIndex + 1, col: 0 });
        } else {
          addRows(1);
          setTimeout(() => setActiveCell({ row: rowIndex + 1, col: 0 }), 50);
        }
      } else if (nextCol < 0) {
        if (rowIndex > 0) setActiveCell({ row: rowIndex - 1, col: COLUMNS.length - 1 });
      } else {
        setActiveCell({ row: rowIndex, col: nextCol });
      }
    }
  };

  useEffect(() => {
    if (activeCell) {
      const input = document.querySelector(`[data-cell="${activeCell.row}-${activeCell.col}"]`);
      if (input) input.focus();
    }
  }, [activeCell]);

  const filledRows = rows.filter(r => r.item_name || r.quantity || r.purchase_cost);
  const validRows = rows.filter(r => r._status === 'valid' || r._status === 'imported');
  const errorRows = rows.filter(r => r._status === 'error' || r._status === 'unmatched');
  const unmatchedRows = rows.filter(r => r._status === 'unmatched');

  return (
    <div className="p-6 max-w-full">
      {/* Hidden file input */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileSelect}
        accept=".csv,.xlsx,.xls"
        className="hidden"
        data-testid="csv-file-input"
      />

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/inventory')} data-testid="batch-import-back">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <FileSpreadsheet className="w-6 h-6 text-blue-600" />
              Batch Import
            </h1>
            <p className="text-sm text-slate-500 mt-0.5">Add stock batches to multiple products at once</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => fileInputRef.current?.click()}
            className="text-blue-700 font-bold hover:text-blue-900 hover:underline text-sm flex items-center gap-1"
            data-testid="import-csv-btn"
          >
            <FileUp className="w-4 h-4" /> Import from CSV / Excel
          </button>
          <Button variant="outline" size="sm" onClick={clearAll} data-testid="batch-import-clear">
            Clear All
          </Button>
          <Button
            className={importResult ? "bg-slate-400 text-white cursor-not-allowed" : "bg-emerald-600 hover:bg-emerald-700 text-white"}
            onClick={handleImport}
            disabled={importing || filledRows.length === 0 || !!importResult}
            data-testid="batch-import-submit"
          >
            <Upload className="w-4 h-4 mr-1" />
            {importing ? 'Importing...' : importResult ? `Imported ${importResult.imported_count} Batch${importResult.imported_count !== 1 ? 'es' : ''}` : `Import ${filledRows.length} Batch${filledRows.length !== 1 ? 'es' : ''}`}
          </Button>
        </div>
      </div>

      {/* Status bar */}
      {(filledRows.length > 0 || importResult) && (
        <div className="flex items-center gap-4 mb-4 text-sm">
          <span className="text-slate-500">{filledRows.length} row{filledRows.length !== 1 ? 's' : ''} filled</span>
          {validRows.length > 0 && <span className="text-emerald-600 flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> {validRows.length} valid</span>}
          {unmatchedRows.length > 0 && <span className="text-red-600 flex items-center gap-1 font-medium"><AlertCircle className="w-3.5 h-3.5" /> {unmatchedRows.length} not found in inventory</span>}
          {errorRows.length > 0 && errorRows.length !== unmatchedRows.length && <span className="text-orange-600 flex items-center gap-1"><AlertCircle className="w-3.5 h-3.5" /> {errorRows.length - unmatchedRows.length} other error{errorRows.length - unmatchedRows.length !== 1 ? 's' : ''}</span>}
          {importResult && <span className="text-blue-600 font-medium">{importResult.imported_count} imported successfully</span>}
        </div>
      )}

      {/* Spreadsheet */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          <div className="overflow-x-auto" ref={tableRef}>
            <table className="border-collapse" style={{ width: 'max-content', minWidth: '100%' }} data-testid="batch-import-table">
              <thead>
                <tr className="bg-slate-700 text-white">
                  <th className="px-2 py-2.5 text-xs font-semibold text-center w-10 border-r border-slate-600">#</th>
                  {COLUMNS.map(col => (
                    <th
                      key={col.key}
                      className="px-2 py-2.5 text-xs font-semibold text-left border-r border-slate-600 relative select-none"
                      style={{ width: `${colWidths[col.key]}px`, minWidth: `${colWidths[col.key]}px` }}
                    >
                      {col.label}
                      <div
                        className="absolute right-0 top-0 bottom-0 w-1.5 cursor-col-resize hover:bg-blue-400/60 active:bg-blue-500/60"
                        onMouseDown={(e) => startResize(e, col.key)}
                        data-testid={`resize-${col.key}`}
                      />
                    </th>
                  ))}
                  <th className="px-2 py-2.5 w-10"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, rowIdx) => {
                  const isUnmatched = row._status === 'unmatched';
                  const isError = row._status === 'error';
                  const isImported = row._status === 'imported';
                  return (
                    <tr
                      key={row.id}
                      className={`border-b ${
                        isImported ? 'bg-emerald-50 border-emerald-100' :
                        row._status === 'valid' ? 'bg-blue-50/30' :
                        rowIdx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'
                      } hover:bg-slate-50`}
                      data-testid={`import-row-${rowIdx}`}
                    >
                      <td className={`px-2 py-0.5 text-center text-xs font-mono border-r ${(isError || isUnmatched) ? 'text-red-500 border-red-200' : 'text-slate-400 border-slate-100'}`}>{rowIdx + 1}</td>
                      {COLUMNS.map((col, colIdx) => (
                        <td
                          key={col.key}
                          className={`px-0.5 py-0.5 border-r relative ${(isError || isUnmatched) && row._error_field === col.key ? 'bg-red-50 border-red-300 ring-1 ring-inset ring-red-300' : 'border-slate-100'}`}
                          style={{ width: `${colWidths[col.key]}px`, minWidth: `${colWidths[col.key]}px` }}
                        >
                          <input
                            data-cell={`${rowIdx}-${colIdx}`}
                            type={col.type}
                            value={row[col.key]}
                            onChange={(e) => updateRow(row.id, col.key, e.target.value)}
                            onKeyDown={(e) => handleKeyDown(e, rowIdx, colIdx)}
                            onFocus={() => {
                              setActiveCell({ row: rowIdx, col: colIdx });
                              if (col.key === 'item_name') handleItemFocus(row.id, row[col.key]);
                              if (col.key === 'location_name') handleLocationFocus(row.id, row[col.key]);
                            }}
                            placeholder={col.placeholder}
                            disabled={isImported}
                            step={col.type === 'number' ? '0.01' : undefined}
                            min={col.type === 'number' ? '0' : undefined}
                            title={(isError || isUnmatched) && row._error_field === col.key ? row._error : undefined}
                            className={`w-full px-2 py-1.5 text-sm border-0 outline-none bg-transparent focus:ring-2 focus:ring-blue-400 focus:bg-white rounded ${
                              isImported ? 'text-slate-400' :
                              (isError || isUnmatched) && row._error_field === col.key ? 'text-red-700 font-semibold' :
                              'text-slate-800'
                            }`}
                            data-testid={`import-cell-${rowIdx}-${col.key}`}
                          />
                          {/* Autocomplete dropdown */}
                          {col.key === 'item_name' && suggestionsRowId === row.id && suggestions.length > 0 && (
                            <div ref={suggestionsRef} className={`absolute z-50 left-0 w-[500px] max-h-60 overflow-y-auto bg-white border border-slate-200 rounded-lg shadow-xl ${rowIdx >= rows.length - 3 ? 'bottom-full mb-1' : 'top-full mt-1'}`}>
                              {suggestions.map(item => (
                                <button
                                  key={item.id}
                                  type="button"
                                  className="w-full text-left px-3 py-2.5 text-sm hover:bg-blue-50 border-b border-slate-50"
                                  onClick={() => selectSuggestion(row.id, item)}
                                  data-testid={`suggestion-${item.id}`}
                                >
                                  <span className="font-medium text-slate-800 break-words leading-snug">{item.name}</span>
                                  {item.sku && <span className="text-xs text-slate-400 ml-2">({item.sku})</span>}
                                </button>
                              ))}
                            </div>
                          )}
                          {/* Location autocomplete dropdown */}
                          {col.key === 'location_name' && locationSuggestionsRowId === row.id && locationSuggestions.length > 0 && (
                            <div ref={locationSuggestionsRef} className={`absolute z-50 left-0 w-[250px] max-h-48 overflow-y-auto bg-white border border-slate-200 rounded-lg shadow-xl ${rowIdx >= rows.length - 3 ? 'bottom-full mb-1' : 'top-full mt-1'}`}>
                              {locationSuggestions.map(loc => (
                                <button
                                  key={loc.id}
                                  type="button"
                                  className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 border-b border-slate-50"
                                  onClick={() => selectLocationSuggestion(row.id, loc)}
                                  data-testid={`loc-suggestion-${loc.id}`}
                                >
                                  <span className="font-medium text-slate-800">{loc.name}</span>
                                </button>
                              ))}
                            </div>
                          )}
                        </td>
                      ))}
                      <td className="px-1 py-0.5 text-center">
                        {!isImported && (
                          <button onClick={() => removeRow(row.id)} className="text-slate-300 hover:text-red-500 transition-colors p-1" data-testid={`import-remove-${rowIdx}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Unmatched items summary */}
          {unmatchedRows.length > 0 && (
            <div className="px-4 py-3 bg-red-50 border-t border-red-200">
              <p className="text-sm font-semibold text-red-700 flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                {unmatchedRows.length} product{unmatchedRows.length !== 1 ? 's' : ''} not found in inventory:
              </p>
              <ul className="mt-1 text-xs text-red-600 space-y-0.5 max-h-24 overflow-y-auto">
                {unmatchedRows.slice(0, 10).map(r => {
                  const idx = rows.findIndex(row => row.id === r.id);
                  return <li key={r.id}>Row {idx + 1}: <span className="font-medium">"{r.item_name}"</span> — correct the name or select from suggestions</li>;
                })}
                {unmatchedRows.length > 10 && <li className="font-medium">... and {unmatchedRows.length - 10} more</li>}
              </ul>
            </div>
          )}

          {/* Error summary (non-unmatched) */}
          {errorRows.length > 0 && errorRows.length !== unmatchedRows.length && (
            <div className="px-4 py-3 bg-orange-50 border-t border-orange-200">
              <p className="text-sm font-medium text-orange-700 flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                {errorRows.length - unmatchedRows.length} row{errorRows.length - unmatchedRows.length !== 1 ? 's' : ''} with other errors
              </p>
            </div>
          )}

          {/* Footer */}
          <div className="px-4 py-3 border-t border-slate-200 bg-slate-50 flex items-center justify-between">
            <span className="text-xs text-slate-400">Tab/Enter to move between cells. Type item name to search inventory.</span>
            <Button variant="ghost" size="sm" onClick={() => addRows(5)} className="text-blue-600 text-xs">
              <Plus className="w-3 h-3 mr-1" /> Add More Rows
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* ============ Field Mapping Dialog ============ */}
      <Dialog open={mappingOpen} onOpenChange={setMappingOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col" data-testid="mapping-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg">
              <FileUp className="w-5 h-5 text-blue-600" />
              Map File Columns
            </DialogTitle>
            <p className="text-sm text-slate-500">Match each column from your file to the corresponding batch field. We've auto-detected what we could.</p>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto space-y-4 mt-4 pr-1">
            {/* Mapping controls */}
            <div className="space-y-3">
              {csvHeaders.map((header, idx) => (
                <div key={idx} className="flex items-center gap-3 bg-slate-50 rounded-lg px-4 py-2.5">
                  <div className="w-1/3">
                    <span className="text-sm font-medium text-slate-700">{header}</span>
                    <div className="text-xs text-slate-400 mt-0.5 truncate">
                      e.g. "{csvPreviewRows[0]?.[idx] || '—'}"
                    </div>
                  </div>
                  <span className="text-slate-300">→</span>
                  <div className="w-1/3">
                    <Select value={fieldMapping[idx] || ''} onValueChange={(val) => setFieldMapping(prev => ({ ...prev, [idx]: val }))}>
                      <SelectTrigger className="h-9" data-testid={`mapping-select-${idx}`}>
                        <SelectValue placeholder="-- Skip --" />
                      </SelectTrigger>
                      <SelectContent>
                        {MAPPABLE_FIELDS.map(f => (
                          <SelectItem key={f.key || 'skip'} value={f.key || 'skip'} data-testid={`mapping-option-${f.key || 'skip'}`}>
                            {f.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {fieldMapping[idx] && fieldMapping[idx] !== 'skip' && (
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
                  )}
                </div>
              ))}
            </div>

            {/* Preview table */}
            <div className="mt-4">
              <Label className="text-xs text-slate-500 mb-2 block">Preview (first {Math.min(csvPreviewRows.length, 5)} rows)</Label>
              <div className="overflow-x-auto border rounded-lg">
                <table className="text-xs w-full">
                  <thead>
                    <tr className="bg-slate-100">
                      {csvHeaders.map((h, i) => (
                        <th key={i} className="px-2 py-1.5 text-left font-medium text-slate-600 border-r border-slate-200 whitespace-nowrap">
                          {h}
                          {fieldMapping[i] && fieldMapping[i] !== 'skip' && (
                            <span className="ml-1 text-blue-600">→ {MAPPABLE_FIELDS.find(f => f.key === fieldMapping[i])?.label}</span>
                          )}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {csvPreviewRows.map((row, ri) => (
                      <tr key={ri} className="border-t border-slate-100">
                        {csvHeaders.map((_, ci) => (
                          <td key={ci} className="px-2 py-1 text-slate-600 border-r border-slate-100 whitespace-nowrap max-w-[200px] truncate">
                            {row[ci] || ''}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <DialogFooter className="mt-4 pt-4 border-t border-slate-200 shrink-0">
            <Button variant="outline" onClick={() => setMappingOpen(false)}>Cancel</Button>
            <Button
              className="bg-blue-600 hover:bg-blue-700 text-white"
              onClick={applyMapping}
              disabled={!Object.values(fieldMapping).some(v => v === 'item_name')}
              data-testid="apply-mapping-btn"
            >
              Load {csvAllRows.length} Row{csvAllRows.length !== 1 ? 's' : ''}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BatchImport;
